import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { overrideSubmissionStatus } from '../../../api/submissions/submissions.api';
import type { SubmissionStatus } from '../../../api/submissions/submissions.api.types';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { useSearchRenewalSubmissions } from '../../../components/hooks/queries/submissions/useSearchRenewalSubmissions';
import { useNotifications } from '../../../_lib/notifications/useNotifications';
import { queries } from '../../../queries';

export const invalidateSubmissionStatusQueries = (
  queryClient: QueryClient,
  submissionId: string
) => {
  queryClient.invalidateQueries(
    queries.submissions.detailsWithConfidence(submissionId).queryKey
  );
  queryClient.invalidateQueries(
    queries.submissions.claimsStatus(submissionId).queryKey
  );
  queryClient.invalidateQueries([useSearchRenewalSubmissions.QUERY_KEY_TOPIC]);
  queryClient.invalidateQueries(queries.submissions.list._def);
  queryClient.invalidateQueries(queries.submissions.workspace._def);
};

/**
 * The notes pane and the notes tab each cache their list. The override writes the reason as a note, so both are
 * refreshed. The note is created through a Kafka event, so a refetch that lands first can still miss it until the
 * next refresh.
 */
export const invalidateSubmissionNotesQueries = (
  queryClient: QueryClient,
  submissionNumber: string
) => {
  queryClient.invalidateQueries(['submissions', submissionNumber, 'notes']);
  queryClient.invalidateQueries(queries.notes.list._def);
};

export interface SystemStatusUpdate {
  readonly status: SubmissionStatus;
  readonly reason: string;
}

/** Sets a status the system normally sets, with the reason that goes in the submission notes. */
export const useSystemStatusUpdate = ({
  id: submissionId,
  submissionNumber,
}: Readonly<{ id: string; submissionNumber: string }>) => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const handleAPIError = useAPIErrorHandler();

  return useMutation({
    mutationFn: ({ status, reason }: SystemStatusUpdate) =>
      overrideSubmissionStatus(submissionId, {
        data: { submissionStatus: status, reason },
      }),
    onSuccess: () => {
      notifications.success('Submission status updated successfully');
      invalidateSubmissionStatusQueries(queryClient, submissionId);
      invalidateSubmissionNotesQueries(queryClient, submissionNumber);
    },
    onError: handleAPIError('Failed to update submission status'),
  });
};
